/**
 * Messages Page — Inbox, Sent, Compose, Thread View
 *
 * Features:
 *   - Inbox/Sent toggle
 *   - Message list: sender, subject, fund/investee context, timestamp, read/unread
 *   - Click message -> view with reply capability
 *   - Compose message dialog: recipient, subject, body, link to fund/investee
 *   - Thread view (nested replies)
 *   - Unread count badge in sidebar navigation
 *
 * Data from:
 *   GET  /api/v1/impact-core/messages/?folder=inbox|sent — message list
 *   GET  /api/v1/impact-core/messages/:id                — single message + thread
 *   POST /api/v1/impact-core/messages/                   — send message
 *   PATCH /api/v1/impact-core/messages/:id/read          — mark as read
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  Send,
  Inbox,
  Plus,
  X,
  ArrowLeft,
  Reply,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { Fund, Investee } from '@/types';

// ─── Types ──────────────────────────────────────────────────────

interface Message {
  id: number;
  subject: string;
  body: string;
  sender_id: number;
  sender_name: string;
  recipient_id: number;
  recipient_name: string;
  fund_id?: number;
  fund_name?: string;
  investee_id?: number;
  investee_name?: string;
  is_read: boolean;
  parent_id?: number;
  created_at: string;
}

interface MessageThread {
  message: Message;
  replies: Message[];
}

interface MessageCreatePayload {
  recipient_id: number;
  subject: string;
  body: string;
  fund_id?: number;
  investee_id?: number;
  parent_id?: number;
}

interface OrgUser {
  id: number;
  full_name: string;
  email: string;
}

type Folder = 'inbox' | 'sent';

// ─── Main Component ─────────────────────────────────────────────

export function MessagesPage() {
  const queryClient = useQueryClient();
  const [folder, setFolder] = useState<Folder>('inbox');
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null);
  const [showCompose, setShowCompose] = useState(false);
  const [replyToId, setReplyToId] = useState<number | null>(null);

  const messagesQuery = useQuery<Message[]>({
    queryKey: ['ic', 'messages', folder],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/messages/?folder=${folder}`);
      return res.data?.data ?? res.data ?? [];
    },
  });

  const threadQuery = useQuery<MessageThread>({
    queryKey: ['ic', 'messages', 'thread', selectedMessageId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/messages/${selectedMessageId}`);
      return res.data?.data ?? res.data ?? { message: null, replies: [] };
    },
    enabled: selectedMessageId !== null,
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.patch(`/api/v1/impact-core/messages/${id}/read`);
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'messages'] });
    },
  });

  const messages = messagesQuery.data ?? [];
  const unreadCount = messages.filter((m) => !m.is_read).length;

  function handleSelectMessage(msg: Message) {
    setSelectedMessageId(msg.id);
    if (!msg.is_read && folder === 'inbox') {
      markReadMutation.mutate(msg.id);
    }
  }

  function handleReply(parentId: number) {
    setReplyToId(parentId);
    setShowCompose(true);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">Messages</h1>
          <p className="text-xs text-ic-text-tertiary">
            {folder === 'inbox' && unreadCount > 0
              ? `${unreadCount} unread message${unreadCount !== 1 ? 's' : ''}`
              : folder === 'inbox'
              ? 'All messages read'
              : `${messages.length} sent message${messages.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button
          onClick={() => {
            setReplyToId(null);
            setShowCompose(true);
          }}
          className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Compose
        </button>
      </div>

      {/* Folder Tabs */}
      <div className="border-b border-ic-border">
        <div className="flex gap-0">
          <FolderBtn
            label="Inbox"
            icon={<Inbox className="h-3.5 w-3.5" />}
            active={folder === 'inbox'}
            onClick={() => { setFolder('inbox'); setSelectedMessageId(null); }}
            badge={unreadCount > 0 ? unreadCount : undefined}
          />
          <FolderBtn
            label="Sent"
            icon={<Send className="h-3.5 w-3.5" />}
            active={folder === 'sent'}
            onClick={() => { setFolder('sent'); setSelectedMessageId(null); }}
          />
        </div>
      </div>

      {/* Content Area */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Message List */}
        <div className={`${selectedMessageId !== null ? 'hidden lg:block' : ''} lg:col-span-1`}>
          <div className="rounded-lg border border-ic-border bg-ic-card overflow-hidden">
            {messagesQuery.isLoading ? (
              <div className="flex h-48 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-ic-text-tertiary" />
              </div>
            ) : messagesQuery.isError ? (
              <div className="flex h-48 items-center justify-center">
                <p className="text-xs text-red-400">Failed to load messages.</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-48 items-center justify-center">
                <p className="text-xs text-ic-text-tertiary">
                  {folder === 'inbox' ? 'Your inbox is empty.' : 'No sent messages.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-ic-border/50">
                {messages.map((msg) => (
                  <button
                    key={msg.id}
                    onClick={() => handleSelectMessage(msg)}
                    className={`w-full text-left px-4 py-3 hover:bg-ic-hover/50 transition-colors ${
                      selectedMessageId === msg.id ? 'bg-ic-hover/50' : ''
                    } ${!msg.is_read && folder === 'inbox' ? 'bg-ic-accent/[0.02]' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      {!msg.is_read && folder === 'inbox' && (
                        <span className="mt-1.5 h-2 w-2 rounded-full bg-ic-accent flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-xs truncate ${
                            !msg.is_read && folder === 'inbox'
                              ? 'font-semibold text-ic-text-primary'
                              : 'text-ic-text-secondary'
                          }`}>
                            {folder === 'inbox' ? msg.sender_name : msg.recipient_name}
                          </span>
                          <span className="text-[9px] text-ic-text-tertiary flex-shrink-0">
                            {formatDistanceToNow(parseISO(msg.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <p className={`text-xs truncate mt-0.5 ${
                          !msg.is_read && folder === 'inbox'
                            ? 'font-medium text-ic-text-primary'
                            : 'text-ic-text-secondary'
                        }`}>
                          {msg.subject}
                        </p>
                        <p className="text-[10px] text-ic-text-tertiary truncate mt-0.5">
                          {msg.body}
                        </p>
                        {(msg.fund_name || msg.investee_name) && (
                          <div className="flex items-center gap-1.5 mt-1">
                            {msg.fund_name && (
                              <StatusBadge label={msg.fund_name} variant="info" size="sm" />
                            )}
                            {msg.investee_name && (
                              <StatusBadge label={msg.investee_name} variant="default" size="sm" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Message Detail / Thread */}
        <div className={`${selectedMessageId === null ? 'hidden lg:block' : ''} lg:col-span-2`}>
          {selectedMessageId === null ? (
            <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-ic-border">
              <div className="text-center">
                <Mail className="mx-auto h-8 w-8 text-ic-text-tertiary/50" />
                <p className="mt-2 text-xs text-ic-text-tertiary">Select a message to read</p>
              </div>
            </div>
          ) : threadQuery.isLoading ? (
            <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
              <Loader2 className="h-5 w-5 animate-spin text-ic-text-tertiary" />
            </div>
          ) : threadQuery.isError || !threadQuery.data?.message ? (
            <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
              <p className="text-xs text-red-400">Failed to load message.</p>
            </div>
          ) : (
            <div className="rounded-lg border border-ic-border bg-ic-card">
              {/* Back button (mobile) */}
              <div className="lg:hidden border-b border-ic-border px-4 py-2">
                <button
                  onClick={() => setSelectedMessageId(null)}
                  className="flex items-center gap-1 text-xs text-ic-text-tertiary hover:text-ic-text-secondary"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </button>
              </div>

              {/* Main message */}
              <MessageView
                message={threadQuery.data.message}
                onReply={() => handleReply(threadQuery.data!.message.id)}
              />

              {/* Thread replies */}
              {threadQuery.data.replies.length > 0 && (
                <div className="border-t border-ic-border">
                  <div className="px-4 py-2 bg-ic-surface">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
                      Replies ({threadQuery.data.replies.length})
                    </span>
                  </div>
                  {threadQuery.data.replies.map((reply) => (
                    <div key={reply.id} className="border-t border-ic-border/50">
                      <MessageView
                        message={reply}
                        onReply={() => handleReply(reply.id)}
                        isReply
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Compose Dialog */}
      {showCompose && (
        <ComposeDialog
          replyToId={replyToId}
          replySubject={
            replyToId && threadQuery.data?.message
              ? `Re: ${threadQuery.data.message.subject}`
              : undefined
          }
          replyRecipientId={
            replyToId && threadQuery.data?.message
              ? threadQuery.data.message.sender_id
              : undefined
          }
          onClose={() => { setShowCompose(false); setReplyToId(null); }}
          onSent={() => {
            setShowCompose(false);
            setReplyToId(null);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'messages'] });
            if (selectedMessageId) {
              void queryClient.invalidateQueries({ queryKey: ['ic', 'messages', 'thread', selectedMessageId] });
            }
          }}
        />
      )}
    </div>
  );
}

// ─── Message View ───────────────────────────────────────────────

function MessageView({
  message,
  onReply,
  isReply,
}: {
  message: Message;
  onReply: () => void;
  isReply?: boolean;
}) {
  return (
    <div className={`px-6 py-4 ${isReply ? 'pl-10' : ''}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className={`font-semibold text-ic-text-primary ${isReply ? 'text-xs' : 'text-sm'}`}>
            {message.subject}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-ic-text-secondary">
              From: <span className="font-medium">{message.sender_name}</span>
            </span>
            <span className="text-[10px] text-ic-text-tertiary">
              To: {message.recipient_name}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] text-ic-text-tertiary">
            {format(parseISO(message.created_at), 'dd MMM yyyy HH:mm')}
          </span>
          <button
            onClick={onReply}
            className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-ic-accent hover:bg-ic-accent/10 transition-colors"
          >
            <Reply className="h-3 w-3" />
            Reply
          </button>
        </div>
      </div>

      {/* Context tags */}
      {(message.fund_name || message.investee_name) && (
        <div className="flex items-center gap-1.5 mb-3">
          {message.fund_name && (
            <StatusBadge label={message.fund_name} variant="info" size="sm" />
          )}
          {message.investee_name && (
            <StatusBadge label={message.investee_name} variant="default" size="sm" />
          )}
        </div>
      )}

      {/* Body */}
      <div className="text-xs text-ic-text-secondary whitespace-pre-wrap leading-relaxed">
        {message.body}
      </div>
    </div>
  );
}

// ─── Compose Dialog ─────────────────────────────────────────────

function ComposeDialog({
  replyToId,
  replySubject,
  replyRecipientId,
  onClose,
  onSent,
}: {
  replyToId: number | null;
  replySubject?: string;
  replyRecipientId?: number;
  onClose: () => void;
  onSent: () => void;
}) {
  const [recipientId, setRecipientId] = useState(replyRecipientId?.toString() ?? '');
  const [subject, setSubject] = useState(replySubject ?? '');
  const [body, setBody] = useState('');
  const [fundId, setFundId] = useState('');
  const [investeeId, setInvesteeId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const usersQuery = useQuery<OrgUser[]>({
    queryKey: ['ic', 'org-users'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/users/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const investeesQuery = useQuery<Investee[]>({
    queryKey: ['ic', 'investees'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/investees/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const mutation = useMutation({
    mutationFn: async (payload: MessageCreatePayload) => {
      const res = await apiClient.post('/api/v1/impact-core/messages/', payload);
      return res.data;
    },
    onSuccess: () => onSent(),
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setErrors({ _general: axiosErr.response?.data?.detail ?? 'Failed to send message.' });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!recipientId) { setErrors({ recipient_id: 'Select a recipient' }); return; }
    if (!subject.trim()) { setErrors({ subject: 'Subject is required' }); return; }
    if (!body.trim()) { setErrors({ body: 'Message body is required' }); return; }

    const payload: MessageCreatePayload = {
      recipient_id: Number(recipientId),
      subject: subject.trim(),
      body: body.trim(),
      fund_id: fundId ? Number(fundId) : undefined,
      investee_id: investeeId ? Number(investeeId) : undefined,
      parent_id: replyToId ?? undefined,
    };

    mutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-ic-accent" />
            <h2 className="text-sm font-semibold text-ic-text-primary">
              {replyToId ? 'Reply' : 'New Message'}
            </h2>
          </div>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          <FormField label="To *" error={errors.recipient_id}>
            <div className="relative">
              <select
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
                className="w-full appearance-none rounded-md border border-ic-border bg-ic-input px-3 py-2 pr-8 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">Select recipient...</option>
                {(usersQuery.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary pointer-events-none" />
            </div>
          </FormField>

          <FormField label="Subject *" error={errors.subject}>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Message subject"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </FormField>

          <FormField label="Message *" error={errors.body}>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="Write your message..."
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none resize-none"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Link to Fund">
              <select
                value={fundId}
                onChange={(e) => setFundId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">None</option>
                {(fundsQuery.data ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </FormField>
            <FormField label="Link to Investee">
              <select
                value={investeeId}
                onChange={(e) => setInvesteeId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">None</option>
                {(investeesQuery.data ?? []).map((inv) => <option key={inv.id} value={inv.id}>{inv.name}</option>)}
              </select>
            </FormField>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="flex items-center gap-1.5 rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            <Send className="h-3 w-3" />
            {mutation.isPending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function FolderBtn({
  label,
  icon,
  active,
  onClick,
  badge,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
        active
          ? 'border-ic-accent text-ic-accent'
          : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
      }`}
    >
      {icon}
      {label}
      {badge != null && badge > 0 && (
        <span className="ml-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-ic-accent px-1 text-[9px] font-bold text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{label}</label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}
